/**
 * Blender-style modifiers as build_mesh idioms. Each works in the build's
 * space (the mesh's transform is baked into its vertices first), keeps the
 * mesh's material slots, and returns the mesh to keep using: the same one,
 * or for array() and mirror() the merged result, which consumes the input.
 * The others rebuild the vertices: UVs survive, while vertex colours and other
 * attributes are dropped with a warning. Results over the triangle budget are
 * refused before they are built.
 */
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { toV3 } from './mesh-idioms';
import { V, bounds, edgeKey, faceCenter, faceNormal, readPoly, writePoly, type Face, type Poly, type V3 } from './mesh-poly';

export interface ModifierDeps {
    register: (mesh: Mesh) => Mesh;
    merge: (meshes: Mesh[], name?: string) => Mesh;
    /** Rebill the triangle budget for a mesh rebuilt in place. */
    retally: (mesh: Mesh, indicesBefore: number) => Mesh;
    /** The build's triangle budget, checked before a modifier builds its result. */
    maxTriangles: number;
    warnings: string[];
}

const AXIS: Record<string, number> = { x: 0, y: 1, z: 2 };

/** A finite number from model code, or the default when it is left out. */
function num(value: unknown, fallback: number, what: string): number {
    if (value === undefined) return fallback;
    if (typeof value !== 'number' || !Number.isFinite(value)) throw new Error(`${what} must be a finite number, got ${JSON.stringify(value)}`);
    return value;
}

function axisOf(axis: unknown, what: string): number {
    const k = typeof axis === 'string' && Object.hasOwn(AXIS, axis) ? AXIS[axis] : undefined;
    if (k === undefined) throw new Error(`${what}: axis must be "x", "y" or "z"`);
    return k;
}

// ── subdivide ─────────────────────────────────────────────

/** One Catmull-Clark (or simple) step: each face becomes one quad per corner. */
function subdivideStep(mesh: Poly, simple: boolean): Poly {
    const { verts, faces } = mesh;
    const edges = new Map<string, { a: number; b: number; faces: number[]; idx: number }>();
    const vEdges: { a: number; b: number; faces: number[] }[][] = verts.map(() => []);
    const vFaces: number[][] = verts.map(() => []);
    const fp = faces.map(f => faceCenter(verts, f.v));
    faces.forEach((f, fi) => {
        f.v.forEach((a, i) => {
            const b = f.v[(i + 1) % f.v.length], k = edgeKey(a, b);
            let e = edges.get(k);
            if (!e) {
                edges.set(k, (e = { a, b, faces: [], idx: 0 }));
                vEdges[a].push(e); vEdges[b].push(e);
            }
            e.faces.push(fi);
            vFaces[a].push(fi);
        });
    });
    const out = verts.map(p => [...p]);
    for (const e of edges.values()) {
        e.idx = out.length;
        out.push(!simple && e.faces.length === 2
            ? V.scale(V.add(V.add(verts[e.a], verts[e.b]), V.add(fp[e.faces[0]], fp[e.faces[1]])), 0.25)
            : V.lerp(verts[e.a], verts[e.b], 0.5));
    }
    const faceBase = out.length;
    out.push(...fp);
    if (!simple) {
        verts.forEach((p, v) => {
            const es = vEdges[v];
            if (!es.length) return;
            // Open boundaries smooth as a cubic B-spline; a corner of three or more stays put.
            const open = es.filter(e => e.faces.length !== 2);
            if (open.length === 2) {
                const o = open.map(e => verts[e.a === v ? e.b : e.a]);
                out[v] = V.scale(V.add(V.scale(p, 6), V.add(o[0], o[1])), 1 / 8);
            } else if (open.length === 0) {
                const n = es.length, fs = [...new Set(vFaces[v])];
                const F = V.scale(fs.reduce((s, f) => V.add(s, fp[f]), [0, 0, 0]), 1 / fs.length);
                const R = V.scale(es.reduce((s, e) => V.add(s, V.lerp(verts[e.a], verts[e.b], 0.5)), [0, 0, 0]), 1 / n);
                out[v] = V.scale(V.add(V.add(F, V.scale(R, 2)), V.scale(p, n - 3)), 1 / n);
            }
        });
    }
    const next: Face[] = [];
    faces.forEach((f, fi) => {
        const n = f.v.length;
        const uvC = f.uv?.reduce((s, u) => [s[0] + u[0] / n, s[1] + u[1] / n], [0, 0]);
        f.v.forEach((v, i) => {
            const nx = f.v[(i + 1) % n], pv = f.v[(i + n - 1) % n];
            const face: Face = {
                v: [v, edges.get(edgeKey(v, nx))!.idx, faceBase + fi, edges.get(edgeKey(pv, v))!.idx],
                slot: f.slot, smooth: simple ? f.smooth : true,
            };
            if (f.uv && uvC) {
                const u = f.uv[i], uN = f.uv[(i + 1) % n], uP = f.uv[(i + n - 1) % n];
                face.uv = [u, [(u[0] + uN[0]) / 2, (u[1] + uN[1]) / 2], uvC, [(u[0] + uP[0]) / 2, (u[1] + uP[1]) / 2]];
            }
            next.push(face);
        });
    });
    return { verts: out, faces: next };
}

// ── bevel ─────────────────────────────────────────────────

type Corner = { kind: 'new'; dir: V3 } | { kind: 'slide'; key: string } | { kind: 'keep' };

/**
 * Offset-and-bridge edge bevel. Every edge sharper than `angle` is cut back
 * `width` into both faces and bridged by `segments` quads on a circular arc.
 * Where a bevelled edge meets an unbevelled one the cut slides along the
 * unbevelled edge. Corner holes close with one n-gon, or with a spherical
 * patch when every edge at the vertex is bevelled. Width is clamped so cuts
 * never cross. Returns the width actually used.
 */
function bevelPoly(mesh: Poly, width: number, segments: number, angleLimit: number): { poly: Poly; width: number } {
    const { verts, faces } = mesh;
    const fn = faces.map(f => faceNormal(verts, f.v));
    const edges = new Map<string, { a: number; b: number; half: { f: number; from: number; to: number }[] }>();
    faces.forEach((f, fi) => f.v.forEach((a, i) => {
        const b = f.v[(i + 1) % f.v.length], k = edgeKey(a, b);
        if (!edges.has(k)) edges.set(k, { a, b, half: [] });
        edges.get(k)!.half.push({ f: fi, from: a, to: b });
    }));
    const sharp = new Set<string>();
    for (const [k, e] of edges) {
        if (e.half.length !== 2 || e.half[0].from === e.half[1].from) continue;
        const ang = Math.acos(Math.max(-1, Math.min(1, V.dot(fn[e.half[0].f], fn[e.half[1].f]))));
        if (ang > angleLimit + 1e-6) sharp.add(k);
    }
    if (!sharp.size) return { poly: mesh, width: 0 };

    // Unit-width corner offsets per face corner, scaled by the clamped width below.
    const corner: Corner[][] = faces.map(() => []);
    const slides = new Map<string, { v: number; sum: V3; n: number }>();
    faces.forEach((f, fi) => {
        const n = f.v.length;
        f.v.forEach((v, i) => {
            const u = f.v[(i + n - 1) % n], x = f.v[(i + 1) % n];
            const sIn = sharp.has(edgeKey(u, v)), sOut = sharp.has(edgeKey(v, x));
            const d1 = V.norm(V.sub(verts[u], verts[v])), d2 = V.norm(V.sub(verts[x], verts[v]));
            const sin = V.len(V.cross(d1, d2));
            if (sIn && sOut) {
                corner[fi][i] = { kind: 'new', dir: sin < 1e-6 ? V.norm(V.cross(fn[fi], d2)) : V.scale(V.add(d1, d2), 1 / sin) };
            } else if (sIn || sOut) {
                const along = sOut ? d1 : d2, other = sOut ? u : x;
                const key = `${v}|${edgeKey(v, other)}`;
                const sl = slides.get(key) ?? { v, sum: [0, 0, 0], n: 0 };
                sl.sum = V.add(sl.sum, V.scale(along, sin < 1e-6 ? 1 : 1 / sin));
                sl.n++;
                slides.set(key, sl);
                corner[fi][i] = { kind: 'slide', key };
            } else corner[fi][i] = { kind: 'keep' };
        });
    });
    const dirOf = (c: Corner): V3 | null => {
        if (c.kind === 'new') return c.dir;
        if (c.kind === 'slide') { const s = slides.get(c.key)!; return V.scale(s.sum, 1 / s.n); }
        return null;
    };

    // Clamp so the two cuts on any edge never cross.
    let w = width;
    const reach = new Map<string, Map<number, number>>();
    faces.forEach((f, fi) => {
        const n = f.v.length;
        f.v.forEach((v, i) => {
            const dir = dirOf(corner[fi][i]);
            if (!dir) return;
            for (const o of [f.v[(i + n - 1) % n], f.v[(i + 1) % n]]) {
                const t = V.dot(dir, V.norm(V.sub(verts[o], verts[v])));
                if (t <= 0) continue;
                const k = edgeKey(v, o);
                const r = reach.get(k) ?? new Map<number, number>();
                r.set(v, Math.max(r.get(v) ?? 0, t));
                reach.set(k, r);
            }
        });
    });
    for (const [k, r] of reach) {
        const [a, b] = k.split(',').map(Number);
        const total = (r.get(a) ?? 0) + (r.get(b) ?? 0);
        if (total > 1e-9) w = Math.min(w, (V.dist(verts[a], verts[b]) / total) * 0.999);
    }

    const out = verts.map(p => [...p]);
    const origin = verts.map((_, i) => i);
    const add = (p: V3, o: number): number => { out.push(p); origin.push(o); return out.length - 1; };
    const slideIdx = new Map<string, number>();
    for (const [key, s] of slides) slideIdx.set(key, add(V.add(verts[s.v], V.scale(s.sum, w / s.n)), s.v));
    const pointOf = faces.map((f, fi) => f.v.map((v, i) => {
        const c = corner[fi][i];
        if (c.kind === 'new') return add(V.add(verts[v], V.scale(c.dir, w)), v);
        if (c.kind === 'slide') return slideIdx.get(c.key)!;
        return v;
    }));

    const next: Face[] = [];
    faces.forEach((f, fi) => {
        const n = f.v.length;
        const loop: number[] = [];
        f.v.forEach((v, i) => {
            if (corner[fi][i].kind !== 'keep') { loop.push(pointOf[fi][i]); return; }
            const sIn = slideIdx.get(`${v}|${edgeKey(v, f.v[(i + n - 1) % n])}`);
            const sOut = slideIdx.get(`${v}|${edgeKey(v, f.v[(i + 1) % n])}`);
            if (sIn !== undefined) loop.push(sIn);
            loop.push(v);
            if (sOut !== undefined) loop.push(sOut);
        });
        const dedup = loop.filter((p, i) => p !== loop[(i + loop.length - 1) % loop.length]);
        if (dedup.length >= 3) next.push({ v: dedup, slot: f.slot, smooth: f.smooth });
    });

    // Arc strips across bevelled edges; profile points shared by strips with the same ends.
    const profiles = new Map<string, number[]>();
    const profile = (A: number, B: number, ctrl: V3, o: number): number[] => {
        const lo = Math.min(A, B), hi = Math.max(A, B), key = `${lo},${hi}`;
        let pts = profiles.get(key);
        if (!pts) {
            const P0 = out[lo], P2 = out[hi];
            const phi = Math.acos(Math.max(-1, Math.min(1, V.dot(V.norm(V.sub(P0, ctrl)), V.norm(V.sub(P2, ctrl))))));
            const wt = Math.sin(phi / 2);
            pts = [lo];
            for (let k = 1; k < segments; k++) {
                // Rational quadratic Bezier: an exact circular arc tangent to both faces.
                const t = k / segments, b0 = (1 - t) ** 2, b1 = 2 * t * (1 - t) * wt, b2 = t * t;
                pts.push(add(V.scale(V.add(V.add(V.scale(P0, b0), V.scale(ctrl, b1)), V.scale(P2, b2)), 1 / (b0 + b1 + b2)), o));
            }
            pts.push(hi);
            profiles.set(key, pts);
        }
        return A === lo ? pts : [...pts].reverse();
    };
    const arcCenters = new Map<number, { sum: V3; n: number }>();
    const rounded = segments > 1;
    for (const k of sharp) {
        const [h1, h2] = edges.get(k)!.half;
        const a = h1.from, b = h1.to;
        const at = (fi: number, v: number): number => pointOf[fi][faces[fi].v.indexOf(v)];
        const A0 = at(h1.f, a), As = at(h2.f, a), B0 = at(h1.f, b), Bs = at(h2.f, b);
        const axis = V.norm(V.sub(verts[b], verts[a]));
        const ctrlAt = (v: number, p: number, q: number): V3 => {
            const proj = (pt: number): V3 => V.add(verts[v], V.scale(axis, V.dot(V.sub(out[pt], verts[v]), axis)));
            return V.lerp(proj(p), proj(q), 0.5);
        };
        const ca = ctrlAt(a, A0, As), cb = ctrlAt(b, B0, Bs);
        const pa = profile(A0, As, ca, a), pb = profile(B0, Bs, cb, b);
        for (let s = 0; s < segments; s++) {
            const q = [pb[s], pa[s], pa[s + 1], pb[s + 1]].filter((p, i, arr) => p !== arr[(i + 3) % 4]);
            if (new Set(q).size >= 3) next.push({ v: q, slot: faces[h1.f].slot, smooth: rounded || faces[h1.f].smooth });
        }
        for (const [v, p0, c, pEnd] of [[a, A0, ca, As], [b, B0, cb, Bs]] as [number, number, V3, number][]) {
            const L = V.dist(out[p0], c);
            const phi = Math.acos(Math.max(-1, Math.min(1, V.dot(V.norm(V.sub(out[p0], c)), V.norm(V.sub(out[pEnd], c))))));
            const centre = V.sub(out[p0], V.scale(fn[h1.f], L * Math.tan(phi / 2)));
            const acc = arcCenters.get(v) ?? { sum: [0, 0, 0], n: 0 };
            arcCenters.set(v, { sum: V.add(acc.sum, centre), n: acc.n + 1 });
        }
    }

    // Holes left at bevelled vertices: boundary loops whose points all come from one vertex.
    const half = new Set<string>();
    for (const f of next) f.v.forEach((p, i) => half.add(`${p}>${f.v[(i + 1) % f.v.length]}`));
    const nextOf = new Map<number, number>();
    for (const f of next) f.v.forEach((p, i) => {
        const q = f.v[(i + 1) % f.v.length];
        if (!half.has(`${q}>${p}`) && origin[p] === origin[q] && (p >= verts.length || q >= verts.length) && !nextOf.has(q)) nextOf.set(q, p);
    });
    const soft = new Set<number>();
    for (const [k, e] of edges) if (!sharp.has(k)) { soft.add(e.a); soft.add(e.b); }
    const slotAt = new Map<number, number>();
    for (const f of faces) for (const v of f.v) if (!slotAt.has(v)) slotAt.set(v, f.slot);
    const visited = new Set<number>();
    for (const start of nextOf.keys()) {
        if (visited.has(start)) continue;
        const loop: number[] = [];
        let p: number | undefined = start;
        while (p !== undefined && !visited.has(p)) { visited.add(p); loop.push(p); p = nextOf.get(p); }
        if (p !== start || loop.length < 3) continue;
        const v = origin[loop[0]], slot = slotAt.get(v) ?? 0;
        const centres = arcCenters.get(v);
        if (!rounded || !centres || soft.has(v)) {
            next.push({ v: loop, slot, smooth: rounded });
            continue;
        }
        // Spherical patch: rings from the hole towards a pole, projected onto the arcs' sphere.
        const S = V.scale(centres.sum, 1 / centres.n);
        const R = loop.reduce((s, i) => s + V.dist(out[i], S), 0) / loop.length;
        const mean = V.scale(loop.reduce((s, i) => V.add(s, out[i]), [0, 0, 0]), 1 / loop.length);
        const C = add(V.add(S, V.scale(V.norm(V.sub(mean, S)), R)), v);
        let ring = loop;
        const rings = Math.max(1, Math.floor(segments / 2));
        for (let r = 1; r < rings; r++) {
            const nr = loop.map(i => add(V.add(S, V.scale(V.norm(V.sub(V.lerp(out[i], out[C], r / rings), S)), R)), v));
            ring.forEach((_, j) => {
                const j2 = (j + 1) % ring.length;
                next.push({ v: [ring[j], ring[j2], nr[j2], nr[j]], slot, smooth: true });
            });
            ring = nr;
        }
        ring.forEach((_, j) => next.push({ v: [ring[j], ring[(j + 1) % ring.length], C], slot, smooth: true }));
    }
    return { poly: { verts: out, faces: next }, width: w };
}

// ── solidify ──────────────────────────────────────────────

/**
 * Shell along angle-weighted vertex normals, each scaled so the walls keep
 * their thickness at corners. offset -1 keeps the surface as the outside
 * and grows inward, +1 grows outward, 0 centres. Open edges get a rim.
 */
function solidifyPoly(mesh: Poly, thickness: number, offset: number, rim: boolean): Poly {
    const { verts, faces } = mesh;
    const fn = faces.map(f => faceNormal(verts, f.v));
    const vn: V3[] = verts.map(() => [0, 0, 0]);
    faces.forEach((f, fi) => f.v.forEach((v, i) => {
        const k = f.v.length, p = verts[v];
        const d1 = V.norm(V.sub(verts[f.v[(i + k - 1) % k]], p)), d2 = V.norm(V.sub(verts[f.v[(i + 1) % k]], p));
        vn[v] = V.add(vn[v], V.scale(fn[fi], Math.acos(Math.max(-1, Math.min(1, V.dot(d1, d2))))));
    }));
    const unit = vn.map(V.norm);
    const minDot = verts.map(() => 1);
    faces.forEach((f, fi) => f.v.forEach(v => { minDot[v] = Math.min(minDot[v], Math.max(0.25, V.dot(unit[v], fn[fi]))); }));
    const n = verts.length;
    const outer = (thickness * (offset + 1)) / 2, inner = (thickness * (offset - 1)) / 2;
    const shell = (d: number): V3[] => verts.map((p, i) => V.add(p, V.scale(unit[i], d / minDot[i])));
    const out: Face[] = [
        ...faces.map(f => ({ ...f, v: [...f.v] })),
        ...faces.map(f => ({ ...f, v: f.v.map(i => i + n).reverse(), uv: f.uv && [...f.uv].reverse() })),
    ];
    if (rim) {
        const count = new Map<string, number>();
        for (const f of faces) f.v.forEach((a, i) => { const k = edgeKey(a, f.v[(i + 1) % f.v.length]); count.set(k, (count.get(k) ?? 0) + 1); });
        for (const f of faces) f.v.forEach((a, i) => {
            const b = f.v[(i + 1) % f.v.length];
            if (count.get(edgeKey(a, b)) === 1) out.push({ v: [b, a, a + n, b + n], slot: f.slot, smooth: false });
        });
    }
    const poly = { verts: [...shell(outer), ...shell(inner)], faces: out };
    if (thickness < 0) for (const f of poly.faces) { f.v.reverse(); f.uv?.reverse(); }
    return poly;
}

// ── the idioms ────────────────────────────────────────────

export function buildModifiers(deps: ModifierDeps): Record<string, unknown> {
    const live = (mesh: unknown, what: string): Mesh => {
        if (!(mesh instanceof Mesh)) throw new Error(`${what}(mesh, …) takes a mesh`);
        if (mesh.isDisposed() || !mesh.getTotalVertices()) {
            throw new Error(`${what}: "${mesh.name}" has no live geometry. Use the mesh the previous step returned; merge() disposes its inputs.`);
        }
        mesh.bakeCurrentTransformIntoVertices();
        return mesh;
    };

    /** Refuse, before building, a result that would blow the triangle budget. */
    const afford = (what: string, triangles: number): void => {
        if (triangles > deps.maxTriangles) {
            throw new Error(`${what} would make ~${Math.round(triangles).toLocaleString()} triangles, over the ${deps.maxTriangles.toLocaleString()} budget. Lower levels/segments/count, or simplify the input.`);
        }
    };

    /**
     * Run a polygon operation on the mesh in place. `growth` estimates
     * triangles out per triangle in; `ngons` reads fanned n-gons whole.
     */
    const reshape = (what: string, target: unknown, op: (p: Poly) => Poly, opts: { tolerance?: number; growth?: number; ngons?: boolean } = {}): Mesh => {
        if (target instanceof Mesh) afford(what, (target.getTotalIndices() / 3) * (opts.growth ?? 1));
        const mesh = live(target, what);
        const before = mesh.getTotalIndices();
        const dropped = writePoly(op(readPoly(mesh, opts.tolerance, opts.ngons)), mesh);
        if (dropped.length) deps.warnings.push(`${what}(${mesh.name}) rebuilt the vertices and dropped ${dropped.join(', ')}; apply vertex colours after modifiers.`);
        return deps.retally(mesh, before);
    };

    /**
     * Catmull-Clark subdivision, `levels` times (0 to 4): every face becomes
     * one quad per corner (x4 for quads), the shape shrinks inside its cage
     * and turns smooth. `simple: true` only adds cuts, which is what deform() needs.
     */
    const subdivide = (mesh: Mesh, levels = 1, opts: { simple?: boolean } = {}): Mesh => {
        const asked = Math.round(num(levels, 1, 'subdivide levels'));
        const n = Math.max(0, Math.min(4, asked));
        if (n !== asked) deps.warnings.push(`subdivide: levels ${asked} clamped to ${n}; levels run 0 to 4.`);
        // A triangle becomes three quads (six triangles) at the first level, four times that after.
        return reshape('subdivide', mesh, poly => {
            let p = poly;
            for (let i = 0; i < n; i++) p = subdivideStep(p, !!opts.simple);
            return p;
        }, { growth: n ? 1.5 * 4 ** n : 1 });
    };

    /** Round every edge sharper than `angle` (radians): `width` metres back into each face, `segments` per arc. */
    const bevel = (mesh: Mesh, opts: { width?: number; segments?: number; angle?: number } = {}): Mesh => {
        const width = Math.max(0, num(opts.width, 0.005, 'bevel width'));
        const segments = Math.max(1, Math.min(16, Math.round(num(opts.segments, 2, 'bevel segments'))));
        const angle = num(opts.angle, Math.PI / 6, 'bevel angle');
        return reshape('bevel', mesh, poly => {
            const { poly: out, width: used } = bevelPoly(poly, width, segments, angle);
            if (used > 0 && used < width - 1e-9) {
                deps.warnings.push(`bevel(${(mesh as { name?: string }).name ?? 'mesh'}): width clamped from ${width} to ${used.toFixed(4)} so the cuts don't overlap.`);
            }
            return out;
        }, { growth: 3 * segments + 1, ngons: true });
    };

    /** Give a surface wall `thickness`: offset -1 grows inward (default), +1 outward, 0 centred; `rim` closes open edges. */
    const solidify = (mesh: Mesh, opts: { thickness?: number; offset?: number; rim?: boolean } = {}): Mesh => {
        const thickness = num(opts.thickness, 0.01, 'solidify thickness'), offset = num(opts.offset, -1, 'solidify offset');
        // Two shells, plus a rim that is small beside them.
        return reshape('solidify', mesh, poly => solidifyPoly(poly, thickness, offset, opts.rim ?? true), { growth: 2 });
    };

    /**
     * Merge vertices closer than `distance`, so seams (a mirror's, a merge's)
     * shade and bevel as one surface. Like the other in-place modifiers it
     * rebuilds the whole mesh: re-triangulated, normals recomputed, vertex
     * colours dropped.
     */
    const weld = (mesh: Mesh, distance = 1e-4): Mesh =>
        reshape('weld', mesh, poly => poly, { tolerance: Math.max(1e-9, num(distance, 1e-4, 'weld distance')) });

    /**
     * Twist (radians, bottom to top) or taper (top scale = 1 + taper) along
     * `axis`, about the mesh's own centre line. One deformation per call. A
     * box needs cuts first: subdivide(m, 3, { simple: true }).
     */
    const deform = (mesh: Mesh, opts: { twist?: number; taper?: number; axis?: 'x' | 'y' | 'z' } = {}): Mesh => {
        const given = (['twist', 'taper'] as const).filter(k => opts[k] !== undefined);
        for (const k of given) num(opts[k], 0, `deform ${k}`);
        if (given.length !== 1) throw new Error('deform(mesh, { twist | taper, axis }) takes exactly one of twist or taper; call it again to stack them.');
        const ax = axisOf(opts.axis ?? 'y', 'deform');
        const [ia, ib] = ax === 1 ? [2, 0] : ax === 0 ? [1, 2] : [0, 1];
        return reshape('deform', mesh, poly => {
            const { min, max } = bounds(poly.verts);
            const span = max[ax] - min[ax] || 1;
            const ca = (min[ia] + max[ia]) / 2, cb = (min[ib] + max[ib]) / 2;
            const verts = poly.verts.map(p => {
                const t = (p[ax] - min[ax]) / span, x = p[ia] - ca, y = p[ib] - cb;
                const q = [...p];
                if (given[0] === 'twist') {
                    const a = opts.twist! * t, c = Math.cos(a), s = Math.sin(a);
                    q[ia] = ca + x * c - y * s;
                    q[ib] = cb + x * s + y * c;
                } else {
                    const s = 1 + opts.taper! * t;
                    q[ia] = ca + x * s;
                    q[ib] = cb + y * s;
                }
                return q;
            });
            return { verts, faces: poly.faces };
        });
    };

    /** `count` copies, each `offset` metres on from the last (default: its own width along X), as one mesh. */
    const array = (mesh: Mesh, opts: { count?: number; offset?: number[] | Vector3 } = {}): Mesh => {
        const count = Math.max(1, Math.round(num(opts.count, 2, 'array count')));
        const offset = opts.offset === undefined ? undefined : toV3(opts.offset);
        if (offset) for (const v of offset.asArray()) num(v, 0, 'array offset');
        if (mesh instanceof Mesh) afford('array', (mesh.getTotalIndices() / 3) * count);
        const source = live(mesh, 'array');
        source.refreshBoundingInfo();
        const box = source.getBoundingInfo().boundingBox;
        const step = offset ?? new Vector3(box.maximum.x - box.minimum.x, 0, 0);
        const copies = [source];
        for (let i = 1; i < count; i++) {
            const copy = deps.register(source.clone(`${source.name}_${i + 1}`));
            copy.makeGeometryUnique();
            copy.bakeTransformIntoVertices(Matrix.Translation(step.x * i, step.y * i, step.z * i));
            copies.push(copy);
        }
        return deps.merge(copies, source.name);
    };

    /** The mesh plus its reflection across the build's x = 0 (or y/z) plane, as one mesh. weld() joins the seam. */
    const mirror = (mesh: Mesh, opts: { axis?: 'x' | 'y' | 'z' } = {}): Mesh => {
        const ax = axisOf(opts.axis ?? 'x', 'mirror');
        if (mesh instanceof Mesh) afford('mirror', (mesh.getTotalIndices() / 3) * 2);
        const source = live(mesh, 'mirror');
        const copy = deps.register(source.clone(`${source.name}_mirror`));
        copy.makeGeometryUnique();
        copy.bakeTransformIntoVertices(Matrix.Scaling(ax === 0 ? -1 : 1, ax === 1 ? -1 : 1, ax === 2 ? -1 : 1));
        return deps.merge([source, copy], source.name);
    };

    return { subdivide, bevel, solidify, weld, deform, array, mirror };
}
